/**
 * PREFILL VALUES THE DEVICE ITSELF CAN ASSERT.
 * ===========================================
 *
 * Everything else in the prefill bundle comes from a Salesforce read, so it is unavailable offline
 * or before a kiosk lookup succeeds. These values are different: the device knows them with no
 * network at all, which is why they are assembled separately from `prefillInputsFromKiosk`.
 *
 * `visit.startTime` was in the prefill catalog from the start, labelled "Device clock at first
 * answer", and the install schema's `visit_start` field already pointed at it — but **nothing ever
 * supplied it**, so the field rendered blank and a technician had to read their own watch and type
 * the time. A catalog key with no producer is indistinguishable from a missing feature at the point
 * of use, which is why this module exists rather than a line inside the kiosk mapper.
 */

/**
 * Local wall-clock time as `HH:MM`, 24-hour, zero-padded.
 *
 * `getHours`/`getMinutes` (not UTC) because the technician's own clock is what the form is asking
 * about, and not `toLocaleTimeString`, which is locale-dependent and would hand back a 12-hour
 * string with AM/PM under an en-US locale — failing the schema's
 * `([01][0-9]|2[0-3]):[0-5][0-9]` pattern on exactly the devices most likely to be used.
 *
 * The target field is `Work_Submission__c.Visit_Start_Time__c`, `string(30)` — a TEXT field, not a
 * DateTime — so `HH:MM` is the shape it already holds today, not an ISO instant.
 */
export function deviceStartTime(now: Date = new Date()): string {
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * The device-asserted half of the prefill inputs.
 *
 * Kept as a map rather than a single value so the next device-known field (battery state, locale,
 * app version) has an obvious home and does not get wedged into the kiosk mapper.
 */
export function deviceInputs(now: Date = new Date()): Record<string, string> {
  return { 'visit.startTime': deviceStartTime(now) };
}
